import React, { useState, useEffect, useRef } from 'react'
import styled, { css } from 'styled-components'
import { motion } from 'framer-motion'
import emailjs from '@emailjs/browser'

const Form = styled(motion.form)<{ $variant: 'dark' | 'light' }>`
  max-width: 600px;
  margin: 0 auto;
  padding: 2rem;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  background: ${({ $variant }) =>
    $variant === 'light' ? '#fff' : 'rgba(255, 255, 255, 0.05)'};
`

const FormGroup = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const Label = styled(motion.label)<{ $variant: 'dark' | 'light' }>`
  font-size: 1rem;
  font-weight: 500;
  color: ${({ $variant }) => ($variant === 'light' ? '#222' : '#fff')};
`

const Input = styled(motion.input)<{ $variant: 'dark' | 'light' }>`
  padding: 0.8rem 1rem;
  border-radius: 8px;
  border: 1px solid
    ${({ $variant }) =>
      $variant === 'light' ? 'rgba(0,0,0,0.15)' : 'rgba(255,255,255,0.1)'};
  background: ${({ $variant }) =>
    $variant === 'light' ? '#fff' : 'rgba(255,255,255,0.05)'};
  color: ${({ $variant }) => ($variant === 'light' ? '#222' : '#fff')};
  font-size: 1rem;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: #4ecdc4;
    background: ${({ $variant }) =>
      $variant === 'light' ? '#f7f7f7' : 'rgba(255,255,255,0.08)'};
    box-shadow: 0 0 25px rgba(78, 205, 196, 0.2);
  }

  &::placeholder {
    color: ${({ $variant }) =>
      $variant === 'light' ? 'rgba(0,0,0,0.4)' : 'rgba(255,255,255,0.5)'};
  }
`

const TextArea = styled(motion.textarea)<{ $variant: 'dark' | 'light' }>`
  padding: 0.8rem 1rem;
  border-radius: 8px;
  border: 1px solid
    ${({ $variant }) =>
      $variant === 'light' ? 'rgba(0,0,0,0.15)' : 'rgba(255,255,255,0.1)'};
  background: ${({ $variant }) =>
    $variant === 'light' ? '#fff' : 'rgba(255,255,255,0.05)'};
  color: ${({ $variant }) => ($variant === 'light' ? '#222' : '#fff')};
  font-size: 1rem;
  min-height: 150px;
  resize: vertical;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: #4ecdc4;
    background: ${({ $variant }) =>
      $variant === 'light' ? '#f7f7f7' : 'rgba(255,255,255,0.08)'};
    box-shadow: 0 0 25px rgba(78, 205, 196, 0.2);
  }

  &::placeholder {
    color: ${({ $variant }) =>
      $variant === 'light' ? 'rgba(0,0,0,0.4)' : 'rgba(255,255,255,0.5)'};
  }
`

const SubmitButton = styled(motion.button)`
  background: linear-gradient(45deg, #4ecdc4, #ff6b6b);
  border: none;
  padding: 1rem 2rem;
  border-radius: 25px;
  color: white;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 1rem;
  position: relative;
  min-width: 150px;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(78, 205, 196, 0.3);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
    background: linear-gradient(45deg, #4ecdc4, #ff6b6b);
    animation: pulse 1.5s infinite;
  }

  @keyframes pulse {
    0% {
      opacity: 0.7;
    }
    50% {
      opacity: 0.5;
    }
    100% {
      opacity: 0.7;
    }
  }

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 20px;
    height: 20px;
    border: 2px solid transparent;
    border-top-color: white;
    border-radius: 50%;
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:disabled::after {
    opacity: 1;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    0% {
      transform: translate(-50%, -50%) rotate(0deg);
    }
    100% {
      transform: translate(-50%, -50%) rotate(360deg);
    }
  }
`

const StatusMessage = styled(motion.div)<{
  $type: 'success' | 'error'
  $variant: 'dark' | 'light'
}>`
  padding: 1rem;
  border-radius: 8px;
  background: ${(props) =>
    props.$type === 'success'
      ? props.$variant === 'light'
        ? 'rgba(78, 205, 196, 0.08)'
        : 'rgba(78, 205, 196, 0.1)'
      : props.$variant === 'light'
      ? 'rgba(255, 107, 107, 0.08)'
      : 'rgba(255, 107, 107, 0.1)'};
  color: ${(props) => (props.$type === 'success' ? '#4ECDC4' : '#ff6b6b')};
  text-align: center;
  margin-top: 1rem;
`

const SuccessNotification = styled.div`
  background: #e6fcf3;
  color: #1a7f5a;
  border: 1px solid #b2f2e5;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1rem;
  text-align: center;
  font-weight: 600;
  font-size: 1rem;
`

const formVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      staggerChildren: 0.1,
    },
  },
}

const itemVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.5,
    },
  },
}

const buttonVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      delay: 0.4,
    },
  },
  hover: {
    scale: 1.05,
    transition: {
      duration: 0.2,
    },
  },
  tap: {
    scale: 0.95,
  },
}

const statusVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.3,
    },
  },
}

interface ContactFormProps {
  onSuccess?: () => void
  defaultMessage?: string
  variant?: 'dark' | 'light'
}

export const ContactForm: React.FC<ContactFormProps> = ({
  onSuccess,
  defaultMessage,
  variant = 'dark',
}) => {
  const formRef = useRef<HTMLFormElement>(null)
  const [status, setStatus] = useState<{
    type: 'success' | 'error'
    message: string
  } | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [message, setMessage] = useState(defaultMessage || '')

  // New: Track all fields for validation
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [title, setTitle] = useState('')

  useEffect(() => {
    // Initialize EmailJS
    emailjs.init({
      publicKey: 'OJCR3jdfkvKpeIz6t',
    })
  }, [])

  // Update message when defaultMessage changes
  useEffect(() => {
    if (defaultMessage) {
      setMessage(defaultMessage)
    }
  }, [defaultMessage])

  // New: Reset fields on success
  useEffect(() => {
    if (status?.type === 'success') {
      setName('')
      setEmail('')
      setTitle('')
      setMessage(defaultMessage || '')
    }
  }, [status, defaultMessage])

  // New: Simple validation
  const isFormValid =
    name.trim() !== '' &&
    email.trim() !== '' &&
    title.trim() !== '' &&
    message.trim() !== '' &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setStatus(null)

    if (!formRef.current) return

    try {
      const response = await emailjs.sendForm(
        'Dzirmal',
        'Dzirmal_Templaate_ID',
        formRef.current,
        'OJCR3jdfkvKpeIz6t'
      )
      setStatus({
        type: 'success',
        message: 'Thank you for your message! I will get back to you soon.',
      })
      formRef.current.reset()
      onSuccess?.()
    } catch (error: any) {
      setStatus({
        type: 'error',
        message: `Error: ${
          error.text ||
          error.message ||
          'Failed to send message. Please try again.'
        }`,
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Form
      ref={formRef}
      onSubmit={handleSubmit}
      variants={formVariants}
      initial='hidden'
      animate='visible'
      $variant={variant}
      style={{ overflow: 'visible' }}>
      {status?.type === 'success' && (
        <SuccessNotification>{status.message}</SuccessNotification>
      )}
      <FormGroup variants={itemVariants}>
        <Label htmlFor='name' $variant={variant}>
          Name
        </Label>
        <Input
          type='text'
          id='name'
          name='name'
          placeholder='Your name'
          required
          $variant={variant}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </FormGroup>

      <FormGroup variants={itemVariants}>
        <Label htmlFor='email' $variant={variant}>
          Email
        </Label>
        <Input
          type='email'
          id='email'
          name='email'
          placeholder='your.email@example.com'
          required
          $variant={variant}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </FormGroup>

      <FormGroup variants={itemVariants}>
        <Label htmlFor='title' $variant={variant}>
          Subject
        </Label>
        <Input
          type='text'
          id='title'
          name='title'
          placeholder='What is this about?'
          required
          $variant={variant}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
      </FormGroup>

      <FormGroup variants={itemVariants}>
        <Label htmlFor='message' $variant={variant}>
          Message
        </Label>
        <TextArea
          id='message'
          name='message'
          placeholder='Your message...'
          required
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          $variant={variant}
        />
      </FormGroup>

      <SubmitButton
        type='submit'
        disabled={isSubmitting || !isFormValid}
        variants={buttonVariants}
        whileHover='hover'
        whileTap='tap'>
        {isSubmitting ? (
          <>
            <span style={{ visibility: 'hidden' }}>Send Message</span>
            <span
              style={{
                position: 'absolute',
                left: '50%',
                transform: 'translateX(-50%)',
              }}>
              Sending...
            </span>
          </>
        ) : (
          'Send Message'
        )}
      </SubmitButton>

      {status && status.type === 'error' && (
        <StatusMessage
          $type={status.type}
          $variant={variant}
          variants={statusVariants}
          initial='hidden'
          animate='visible'>
          {status.message}
        </StatusMessage>
      )}
    </Form>
  )
}
